import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const locale = "sr";

export const groupsManagement = createApi({
    reducerPath: "groupsManagement",
    baseQuery: fetchBaseQuery({
        baseUrl: `${BASE_URL}`,
        prepareHeaders: (headers) => {
            const jwtToken = localStorage.getItem("userToken") || sessionStorage.getItem("userToken");

            if (jwtToken) {
                headers.set("authorization", `Bearer ${jwtToken}`);
            }

            return headers;
        },
    }),
    endpoints: (builder) => ({
        groupsList: builder.mutation({
            query: (body) => {
                let url = `v1/${locale}/groups`;

                for (const property in body) {
                    if ((property && body[property]) || (property === "startPage" && body[property] === 0)) {
                        if (url?.includes("?")) {
                            url += `&${property}=${body[property]}`;
                        } else {
                            url += `?${property}=${body[property]}`;
                        }
                    }
                }

                return {
                    url: url,
                    method: "GET",
                };
            },
        }),
        viewGroup: builder.mutation({
            query: (groupId) => ({
                url: `v1/${locale}/groups/${groupId}`,
                method: "GET",
            }),
        }),

        addGroup: builder.mutation({
            query: (body) => ({
                url: `v1/${locale}/groups`,
                method: "POST",
                body: body,
            }),
        }),

        updateGroup: builder.mutation({
            query: ({ body, groupId }) => ({
                url: `v1/${locale}/groups/${groupId}`,
                method: "PATCH",
                body: body,
            }),
        }),

        deleteGroup: builder.mutation({
            query: (groupId) => ({
                url: `v1/${locale}/groups/${groupId}`,
                method: "DELETE",
            }),
        }),

        listGroupDates: builder.mutation({
            query: (groupId) => ({
                url: `v1/${locale}/groups/${groupId}/dates`,
                method: "GET",
            }),
        }),

        addGroupDates: builder.mutation({
            query: ({ body, groupId }) => ({
                url: `v1/${locale}/groups/${groupId}/dates`,
                method: "POST",
                body: body,
            }),
        }),

        viewGroupDate: builder.mutation({
            query: ({dateId, groupId}) => {
                return {
                    url: `v1/${locale}/groups/${groupId}/dates/${dateId}`,
                    method: "GET",
                }
            },
        }),

        deleteGroupDate: builder.mutation({
            query: ({dateId, groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/dates/${dateId}`,
                method: "DELETE",
            }),
        }),

        listGroupParticipants: builder.mutation({
            query: (groupId) => ({
                url: `v1/${locale}/groups/${groupId}/participants`,
                method: "GET",
            }),
        }),

        addGroupParticipants: builder.mutation({
            query: ({ body, groupId }) => ({
                url: `v1/${locale}/groups/${groupId}/participants`,
                method: "POST",
                body: body,
            }),
        }),

        deleteGroupParticipant: builder.mutation({
            query: ({participantId, groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/participants/${participantId}`,
                method: "DELETE",
            }),
        }),

        notesList: builder.mutation({
            query: (groupId) => ({
                url: `v1/${locale}/groups/${groupId}/notes`,
                method: "GET",
            }),
        }),
        viewNote: builder.mutation({
            query: ({ groupId, noteId }) => ({
                url: `v1/${locale}/groups/${groupId}/notes/${noteId}`,
                method: "GET",
            }),
        }),
        addNote: builder.mutation({
            query: ({ body, groupId }) => ({
                url: `v1/${locale}/groups/${groupId}/notes`,
                method: "POST",
                body: body,
            }),
        }),
        deleteNote: builder.mutation({
            query: ({ groupId, noteId }) => ({
                url: `v1/${locale}/groups/${groupId}/notes/${noteId}`,
                method: "DELETE",
            }),
        }),
        addDocument: builder.mutation({
            query: ({ file, groupId }) => {
                let formData = new FormData();
                formData.append("file", file);

                return {
                    url: `v1/${locale}/groups/${groupId}/documents`,
                    method: "POST",
                    body: formData
                }
            },
        }),
        listDocuments: builder.mutation({
            query: (groupId) => ({
                url: `v1/${locale}/groups/${groupId}/documents`,
                method: "GET",
            }),
        }),
        deleteDocument: builder.mutation({
            query: ({groupId, documentId}) => ({
                url: `v1/${locale}/groups/${groupId}/documents/${documentId}`,
                method: "DELETE",
            }),
        }),
        downloadDocument: builder.mutation({
            query: ({groupId, documentId}) => ({
                url: `v1/${locale}/groups/${groupId}/documents/${documentId}`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
        }),
        listAttendances: builder.mutation({
            query: ({groupId, body}) => {
                let url = `v1/${locale}/groups/${groupId}/attendances`;

                for (const property in body) {
                    if ((property && body[property]) || (property === "startPage" && body[property] === 0)) {
                        if (url?.includes("?")) {
                            url += `&${property}=${body[property]}`;
                        } else {
                            url += `?${property}=${body[property]}`;
                        }
                    }
                }
                return {
                    url: url,
                    method: "GET",
                };
            },
        }),
        markAsChecked: builder.mutation({
            query: ({ groupId, studentId, calendarId }) => ({
                url: `v1/${locale}/groups/${groupId}/attendances/mark-as-checked/${studentId}/${calendarId}`,
                method: "PATCH"
            }),
        }),
        markAsUnchecked: builder.mutation({
            query: ({ groupId, studentId, calendarId }) => ({
                url: `v1/${locale}/groups/${groupId}/attendances/mark-as-unchecked/${studentId}/${calendarId}`,
                method: "PATCH"
            }),
        }),
        plansList: builder.mutation({
            query: (groupId) => ({
                url: `v1/${locale}/groups/${groupId}/plans`,
                method: "GET",
            }),
        }),
        viewPlan: builder.mutation({
            query: ({groupId, planId}) => ({
                url: `v1/${locale}/groups/${groupId}/plans/${planId}`,
                method: "GET",
            }),
        }),
        addPlan: builder.mutation({
            query: ({body, groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/plans`,
                method: "POST",
                body: body,
            }),
        }),
        updatePlan: builder.mutation({
            query: ({ body, groupId, planId }) => ({
                url: `v1/${locale}/groups/${groupId}/plans/${planId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        deletePlan: builder.mutation({
            query: ({groupId, planId}) => ({
                url: `v1/${locale}/groups/${groupId}/plans/${planId}`,
                method: "DELETE",
            }),
        }),
        approvePlan: builder.mutation({
            query: ({groupId, planId}) => ({
                url: `v1/${locale}/groups/${groupId}/plans/${planId}/approve-one`,
                method: "PATCH",
            }),
        }),
        addPlanNote: builder.mutation({
            query: ({body, planId}) => ({
                url: `v1/${locale}/groups/plans/${planId}/notes`,
                method: "POST",
                body: body,
            }),
        }),
        planNoteList: builder.mutation({
            query: (planId) => ({
                url: `v1/${locale}/groups/plans/${planId}/notes`,
                method: "GET",
            }),
        }),
        updatePlanNote: builder.mutation({
            query: ({ body, groupPlanNoteId }) => ({
                url: `v1/${locale}/groups/plan/note/${groupPlanNoteId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        viewPlanNote: builder.mutation({
            query: ({groupPlanNoteId}) => ({
                url: `v1/${locale}/groups/plan/note/${groupPlanNoteId}`,
                method: "GET",
            }),
        }),
        addProgress: builder.mutation({
            query: ({body, groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/progress`,
                method: "POST",
                body: body,
            }),
        }),
        progressList: builder.mutation({
            query: ({groupId, classNumber}) => ({
                url: `v1/${locale}/groups/${groupId}/progress/${classNumber}`,
                method: "GET",
            }),
        }),
        updateProgress: builder.mutation({
            query: ({ body, groupProgressId }) => ({
                url: `v1/${locale}/groups/progress/${groupProgressId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        addProgressNote: builder.mutation({
            query: ({body, groupProgressId}) => ({
                url: `v1/${locale}/groups/progress/${groupProgressId}/notes`,
                method: "POST",
                body: body,
            }),
        }),
        progressNoteList: builder.mutation({
            query: ({groupProgressId}) => ({
                url: `v1/${locale}/groups/progress/${groupProgressId}/notes`,
                method: "GET",
            }),
        }),
        updateProgressNote: builder.mutation({
            query: ({ body, groupProgressNoteId }) => ({
                url: `v1/${locale}/groups/progress/note/${groupProgressNoteId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        viewProgressNote: builder.mutation({
            query: ({groupProgressNoteId}) => ({
                url: `v1/${locale}/groups/progress/note/${groupProgressNoteId}`,
                method: "GET",
            }),
        }),
        addProgressTests: builder.mutation({
            query: ({body, groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/progress-tests`,
                method: "POST",
                body: body,
            }),
        }),
        progressTestsList: builder.mutation({
            query: ({groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/progress-tests`,
                method: "GET",
            }),
        }),
        updateProgressTests: builder.mutation({
            query: ({ body, groupProgressTestsId }) => ({
                url: `v1/${locale}/groups/progress-tests/${groupProgressTestsId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        viewProgressTests: builder.mutation({
            query: ({groupProgressTestsId}) => ({
                url: `v1/${locale}/groups/progress-tests/${groupProgressTestsId}`,
                method: "GET",
            }),
        }),
        deleteProgressTests: builder.mutation({
            query: ({groupProgressTestsId}) => ({
                url: `v1/${locale}/groups/progress-tests/${groupProgressTestsId}`,
                method: "DELETE",
            }),
        }),
        addProgressTest: builder.mutation({
            query: ({body, groupProgressTestsId}) => ({
                url: `v1/${locale}/groups/progress-test/${groupProgressTestsId}`,
                method: "POST",
                body: body,
            }),
        }),
        progressTestList: builder.mutation({
            query: ({groupProgressTestsId}) => ({
                url: `v1/${locale}/groups/progress-test/${groupProgressTestsId}`,
                method: "GET",
            }),
        }),
        updateProgressTest: builder.mutation({
            query: ({ body, testId }) => ({
                url: `v1/${locale}/groups/progress-test/${testId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        canGroupStart: builder.mutation({
            query: ({groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/can-start`,
                method: "GET",
            }),
        }),

        addReport: builder.mutation({
            query: ({body, groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/reports`,
                method: "POST",
                body: body,
            }),
        }),
        reportsList: builder.mutation({
            query: ({groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/reports`,
                method: "GET",
            }),
        }),
        deleteReport: builder.mutation({
            query: ({groupId, reportId}) => ({
                url: `v1/${locale}/groups/${groupId}/reports/${reportId}`,
                method: "DELETE",
            }),
        }),
        updateReport: builder.mutation({
            query: ({ body, groupId, reportId }) => ({
                url: `v1/${locale}/groups/${groupId}/reports/${reportId}`,
                method: "PATCH",
                body: body,
            }),
        }),
        viewReport: builder.mutation({
            query: ({groupId, reportId}) => ({
                url: `v1/${locale}/groups/${groupId}/reports/${reportId}`,
                method: "GET",
            }),
        }),
        downloadReport: builder.mutation({
            query: ({reportId, studentId}) => ({
                url: `v1/${locale}/groups/report/${reportId}/student/${studentId}/preview-one`,
                method: "GET",
                responseHandler: async (response) => response.blob(),
            }),
        }),
        verifyReport: builder.mutation({
            query: ({reportId, studentId }) => ({
                url: `v1/${locale}/groups/report/${reportId}/student/${studentId}/verify-one`,
                method: "PATCH",
            }),
        }),
        sendReport: builder.mutation({
            query: ({reportId, studentId }) => ({
                url: `v1/${locale}/groups/report/${reportId}/student/${studentId}/send-one`,
                method: "PATCH",
            }),
        }),

        viewReportStudent: builder.mutation({
            query: ({reportId, studentId}) => ({
                url: `v1/${locale}/groups/report/${reportId}/student/${studentId}`,
                method: "GET",
            }),
        }),

        addReportStudent: builder.mutation({
            query: ({reportId, studentId, body}) => ({
                url: `v1/${locale}/groups/report/${reportId}/student/${studentId}/add`,
                method: "POST",
                body: body
            }),
        }),

        editReportStudent: builder.mutation({
            query: ({reportId, studentId, groupReportStudentId, body}) => ({
                url: `v1/${locale}/groups/report/${reportId}/student/${studentId}/edit/${groupReportStudentId}`,
                method: "PATCH",
                body: body
            }),
        }),

        viewProfessor: builder.mutation({
            query: ({groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/professor`,
                method: "GET",
            }),
        }),
        addProfessor: builder.mutation({
            query: ({body, groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/professor`,
                method: "POST",
                body: body,
            }),
        }),
        updateProfessor: builder.mutation({
            query: ({ body, groupId }) => ({
                url: `v1/${locale}/groups/${groupId}/professor`,
                method: "PATCH",
                body: body,
            }),
        }),

        cancelClass: builder.mutation({
            query: ({ body, groupId, cancelCalendarId }) => ({
                url: `v1/${locale}/groups/${groupId}/cancel-class/${cancelCalendarId}`,
                method: "PATCH",
                body: body,
            }),
        }),

        startGroup: builder.mutation({
            query: ({ groupId }) => ({
                url: `v1/${locale}/groups/${groupId}/start`,
                method: "PATCH",
            }),
        }),

        canGroupArchive: builder.mutation({
            query: ({groupId}) => ({
                url: `v1/${locale}/groups/${groupId}/can-archive`,
                method: "GET",
            }),
        }),

        archiveGroup: builder.mutation({
            query: ({ groupId, body }) => ({
                url: `v1/${locale}/groups/${groupId}/archive`,
                method: "PATCH",
                body: body
            }),
        }),

        exportGroups: builder.mutation({
            query: () => ({
                url: `v1/${locale}/export/groups`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
        }),

        archiveParticipant: builder.mutation({
            query: ({ groupId, participantId, body }) => ({
                url: `v1/${locale}/groups/${groupId}/participants/${participantId}/archive`,
                method: "PATCH",
                body: body
            }),
        }),
        viewArchiveNoteParticipant: builder.mutation({
            query: ({ groupId, participantId }) => ({
                url: `v1/${locale}/groups/${groupId}/participants/${participantId}/view-archive-note`,
                method: "GET",
            }),
        }),
    }),
});

export const { 
    useGroupsListMutation, 
    useDeleteGroupMutation, 
    useAddGroupMutation, 
    useViewGroupMutation, 
    useUpdateGroupMutation,
    useListGroupDatesMutation,
    useAddGroupDatesMutation,
    useViewGroupDateMutation,
    useDeleteGroupDateMutation,
    useListGroupParticipantsMutation,
    useAddGroupParticipantsMutation,
    useDeleteGroupParticipantMutation,
    useNotesListMutation,
    useViewNoteMutation,
    useAddNoteMutation,
    useDeleteNoteMutation,
    useAddDocumentMutation,
    useListDocumentsMutation,
    useDeleteDocumentMutation,
    useDownloadDocumentMutation,
    useListAttendancesMutation,
    useMarkAsCheckedMutation,
    useMarkAsUncheckedMutation,
    usePlansListMutation,
    useViewPlanMutation,
    useAddPlanMutation,
    useDeletePlanMutation,
    useUpdatePlanMutation,
    useApprovePlanMutation,
    useAddProgressMutation,
    useProgressListMutation,
    useUpdateProgressMutation,
    useAddProgressNoteMutation,
    useProgressNoteListMutation,
    useUpdateProgressNoteMutation,
    useViewProgressNoteMutation,

    useAddProgressTestsMutation,
    useDeleteProgressTestsMutation,
    useProgressTestsListMutation,
    useUpdateProgressTestsMutation,
    useViewProgressTestsMutation,

    useAddProgressTestMutation,
    useProgressTestListMutation,
    useUpdateProgressTestMutation,

    useCanGroupStartMutation,

    useReportsListMutation,
    useAddReportMutation,
    useDeleteReportMutation,
    useUpdateReportMutation,
    useViewReportMutation,
    useDownloadReportMutation,
    useAddReportStudentMutation,
    useEditReportStudentMutation,

    useSendReportMutation,
    useVerifyReportMutation,
    useViewReportStudent,

    useAddProfessorMutation,
    useUpdateProfessorMutation,
    useViewProfessorMutation,

    useCancelClassMutation,

    useStartGroupMutation,

    useAddPlanNoteMutation,
    usePlanNoteListMutation,
    useUpdatePlanNoteMutation,
    useViewPlanNoteMutation,

    useArchiveGroupMutation,
    useCanGroupArchiveMutation,

    useExportGroupsMutation,

    useArchiveParticipantMutation,
    useViewArchiveNoteParticipantMutation
} = groupsManagement;
